import { HomeCard, HomeCardHead } from "@/components/dashboard/home-card";
import { GiveFeedbackDialog } from "@/components/reviews/GiveFeedbackDialog";
import { Button } from "@/components/ui/button";
import {
  type ProjectFeedbackPrompt,
  useDismissProjectPrompt,
  useMyProjectPrompts,
} from "@/hooks/use-adhoc-feedback";

/**
 * "Your project wrapped up — anything to say about the people you worked
 * with?" — EPIC-068 WI-249 (AC-712 to AC-714).
 *
 * Absent unless there is something to show, and never blocks anything: it is a
 * card on the home page that can be put away (AC-714). Each colleague opens the
 * ordinary Give feedback dialog with the person and the project already filled
 * in, so what is asked is exactly what the dialog already asks.
 */
export function ProjectFeedbackPromptCard() {
  const { data } = useMyProjectPrompts();
  const prompts = data ?? [];
  if (prompts.length === 0) return null;

  return (
    <HomeCard>
      <HomeCardHead title="Say something about the people you worked with" />
      <ul className="divide-y">
        {prompts.map((prompt) => (
          <PromptRow key={prompt.id} prompt={prompt} />
        ))}
      </ul>
    </HomeCard>
  );
}

function PromptRow({ prompt }: { prompt: ProjectFeedbackPrompt }) {
  const dismiss = useDismissProjectPrompt();
  return (
    <li className="space-y-2.5 px-5 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-medium text-sm">{prompt.title} has wrapped up</p>
          <p className="text-muted-foreground text-sm">
            Quick and optional — it's freshest right now.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={dismiss.isPending}
          onClick={() => dismiss.mutate(prompt.id)}
        >
          Not now
        </Button>
      </div>
      <ul className="flex flex-wrap gap-2">
        {prompt.colleagues.map((colleague) => (
          <li key={colleague.personId}>
            <GiveFeedbackDialog
              initialSubjectPersonId={colleague.personId}
              {...(prompt.nosProjectId
                ? {
                    initialProject: {
                      projectId: prompt.nosProjectId,
                      projectName: prompt.projectName ?? prompt.title,
                    },
                  }
                : {})}
              trigger={
                <Button variant="outline" size="sm">
                  {colleague.name}
                </Button>
              }
            />
          </li>
        ))}
      </ul>
    </li>
  );
}
